import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact Us" },
];

function useSASTTime() {
  const [t, setT] = useState("");
  useEffect(() => {
    const fmt = () => {
      try {
        const d = new Date().toLocaleTimeString("en-ZA", {
          timeZone: "Africa/Johannesburg",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
        setT(d);
      } catch {
        setT("");
      }
    };
    fmt();
    const id = setInterval(fmt, 30 * 1000);
    return () => clearInterval(id);
  }, []);
  return t;
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const loc = useLocation();
  const sast = useSASTTime();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Light theme: header is always solid (never transparent). Dark theme keeps the float-over-hero behaviour.
  const isLight = theme === "light";
  const isFloating = !isLight && !scrolled;
  const navColor = isFloating ? "#FDFBF7" : "var(--fg)";
  const navSoftColor = isFloating ? "rgba(253,251,247,0.78)" : "var(--fg-soft)";

  return (
    <header
      data-testid="main-nav"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 backdrop-blur-xl"
      style={{
        background: isFloating ? "transparent" : "color-mix(in srgb, var(--bg) 88%, transparent)",
        borderBottom: isFloating ? "none" : "1px solid var(--border-subtle)",
        color: navColor,
      }}
    >
      {/* Top utility strip */}
      <div
        className="hidden md:block border-b transition-opacity duration-500"
        style={{
          borderColor: isFloating ? "rgba(253,251,247,0.18)" : "var(--border-subtle)",
          background: isFloating ? "rgba(5,9,20,0.15)" : "transparent",
          backdropFilter: isFloating ? "blur(6px)" : "none",
        }}
      >
        <div className="container-x flex items-center justify-between py-2 text-[10px] tracking-[0.28em] uppercase" style={{ color: navSoftColor }}>
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <MapPin size={11} style={{ color: "var(--gicp-gold)" }} />
              Menlyn Maine · Pretoria
            </span>
            <span className="opacity-40">|</span>
            <a
              href="mailto:investor@gicp.co.za"
              data-testid="utility-email"
              className="flex items-center gap-2 hover:text-[var(--gicp-gold)] transition-colors"
            >
              <Mail size={11} style={{ color: "var(--gicp-gold)" }} />
              investor@gicp.co.za
            </a>
          </div>
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2" data-testid="utility-status">
              <span className="relative inline-flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: "var(--gicp-gold)" }} />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ background: "var(--gicp-gold)" }} />
              </span>
              Capital · Open
            </span>
            <span className="opacity-40">|</span>
            <span className="tabular" data-testid="utility-time">{sast || "— : —"} SAST</span>
          </div>
        </div>
      </div>

      {/* Main row */}
      <div className="container-x flex items-center justify-between py-4 md:py-5 relative">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <img
            src="https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/gbuv6jqj_67e2e625-d9d9-4015-b3e4-a5ef601ac92d.png"
            alt="Global Investment Capital Partners"
            className="h-14 md:h-20 w-auto object-contain select-none transition-all duration-500 group-hover:scale-[1.03]"
            style={{
              filter: isFloating
                ? "drop-shadow(0 0 14px rgba(255,255,255,0.85)) drop-shadow(0 0 36px rgba(255,255,255,0.55))"
                : "none",
            }}
            draggable="false"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 px-2 py-1.5 rounded-full" style={{
          background: isFloating ? "rgba(5,9,20,0.18)" : "color-mix(in srgb, var(--bg-soft) 60%, transparent)",
          border: isFloating ? "1px solid rgba(253,251,247,0.12)" : "1px solid var(--border-subtle)",
          backdropFilter: isFloating ? "blur(8px)" : "none",
          transition: "all 500ms ease",
          color: navColor,
        }} data-testid="desktop-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-[11px] tracking-[0.22em] uppercase transition-colors duration-300 rounded-full ${
                  isActive ? "text-[var(--gicp-gold)]" : "hover:text-[var(--gicp-gold)]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative z-10">{l.label}</span>
                  {isActive && (
                    <span
                      className="absolute inset-0 rounded-full"
                      style={{ background: "color-mix(in srgb, var(--gicp-gold) 12%, transparent)", border: "1px solid var(--border-gold)" }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            aria-label="Toggle theme"
            className="relative w-10 h-10 inline-flex items-center justify-center transition-colors hover:text-[var(--gicp-gold)] group"
            style={{ color: navColor, background: "transparent", border: "none" }}
          >
            <span className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: "color-mix(in srgb, var(--gicp-gold) 10%, transparent)" }} />
            <span className="relative">
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </span>
          </button>
          <a
            href="/gicp.pdf"
            download="GICP-Profile.pdf"
            data-testid="nav-cta"
            className="hidden lg:inline-flex items-center gap-2 group relative overflow-hidden px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-medium"
            style={{ background: "var(--gicp-gold)", color: "#FDFBF7", border: "1px solid var(--gicp-gold)" }}
          >
            <span className="relative z-10">Download GICP Profile</span>
            <ArrowUpRight size={14} className="relative z-10 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            <span
              className="absolute inset-0 -translate-x-full group-hover:translate-x-0 transition-transform duration-500"
              style={{ background: "var(--gicp-navy-950)" }}
            />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden w-10 h-10 inline-flex items-center justify-center border"
            style={{ borderColor: "var(--border-subtle)", color: "var(--fg)" }}
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Gold accent line */}
      {scrolled && (
        <div
          aria-hidden="true"
          style={{
            height: 1,
            background: "linear-gradient(90deg, transparent 0%, var(--gicp-gold) 50%, transparent 100%)",
            opacity: 0.4,
          }}
        />
      )}

      {open && (
        <div
          className="lg:hidden border-t"
          style={{ borderColor: "var(--border-subtle)", background: "var(--bg)" }}
          data-testid="mobile-menu"
        >
          <div className="container-x py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className="text-sm tracking-[0.22em] uppercase"
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" className="btn-gold w-fit" data-testid="mobile-cta">
              Download GICP Profile
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
